import { Project } from '../types';
import { escapeCSVField } from './ProjectUtils';

/**
 * Helper function to parse CSV properly (handles quoted fields with commas and newlines)
 * @param csvContent The CSV content to parse
 * @returns Object containing headers and rows
 */
export const parseCSV = (csvContent: string): { headers: string[], rows: string[][] } => {
  // Normalize line endings
  const content = csvContent.replace(/\r\n?/g, '\n');
  
  const result: string[][] = [];
  let row: string[] = [];
  let inQuotes = false;
  let currentField = '';
  
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const nextChar = i < content.length - 1 ? content[i + 1] : '';
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Double quotes inside quotes - add a single quote to the field
        currentField += '"';
        i++; // Skip the next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      row.push(currentField);
      currentField = '';
    } else if (char === '\n' && !inQuotes) {
      // End of row
      row.push(currentField);
      result.push(row);
      row = [];
      currentField = '';
    } else {
      // Regular character
      currentField += char;
    }
  }
  
  // Handle the last field
  if (currentField || row.length > 0) {
    row.push(currentField);
    result.push(row);
  }
  
  // Extract headers and rows
  const headers = result.length > 0 ? result[0] : [];
  const dataRows = result.length > 1 ? result.slice(1) : [];
  
  return {
    headers,
    rows: dataRows
  };
};

/**
 * Generate CSV content for project settings
 * @param project The project to export settings from
 * @returns CSV content as a string
 */
export const generateProjectSettingsCSV = (project: Project | null): string | null => {
  if (!project) return null;

  // Define fields to export
  const fields = [
    'name', 'base_prompt', 'intro_message', 'model_name', 'chat_type',
    'rag_vector_db', 'rag_confidence_threshold', 'rag_top_k', 'rag_token_limit'
  ];
  
  // Create header row
  const headerRow = fields.join(',');
  
  // Create data row
  const values = fields.map(field => {
    // Handle special fields or optional values
    if (field === 'rag_vector_db' && project.chat_type !== 'rag') {
      return '';
    } else if (['rag_confidence_threshold', 'rag_top_k', 'rag_token_limit'].includes(field) && project.chat_type !== 'rag') {
      return '';
    }
    
    // Get the value, handling undefined values
    const value = project[field as keyof Project];
    return escapeCSVField(value !== undefined && value !== null ? String(value) : '');
  });
  
  const contentRow = values.join(',');
  
  // Combine header and content rows
  return [headerRow, contentRow].join('\n');
};

/**
 * Parse CSV content for project settings
 * @param content The CSV content to parse
 * @returns An object with the project settings
 */
export const parseProjectSettings = (content: string): Record<string, string | number> | null => {
  try {
    const { headers, rows } = parseCSV(content);
    if (rows.length === 0) return null;
    
    const settings: Record<string, string | number> = {};
    const row = rows[0]; // Take first row
    
    headers.forEach((header, index) => {
      if (index < row.length) {
        const value = row[index].trim();
        
        // Convert numeric values
        if (['rag_confidence_threshold', 'rag_top_k', 'rag_token_limit'].includes(header) && value !== '') {
          settings[header] = Number(value);
        } else {
          settings[header] = value;
        }
      }
    });
    
    return settings;
  } catch (error) {
    console.error('Error parsing CSV:', error);
    return null;
  }
};

/**
 * Import project settings from a file
 * @param file The file to import settings from
 * @returns Promise that resolves to parsed settings or null if error
 */
export const importProjectSettingsFromFile = (file: File): Promise<Record<string, string | number> | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        if (!content) {
          resolve(null);
          return;
        }
        
        const settings = parseProjectSettings(content);
        resolve(settings);
      } catch (error) {
        console.error('Error reading file:', error);
        resolve(null);
      }
    };
    
    reader.onerror = () => {
      console.error('Error reading file');
      resolve(null);
    };
    
    reader.readAsText(file);
  });
};

/**
 * Export project settings as a CSV file
 * @param project The project to export settings from
 */
export const exportProjectSettings = (project: Project | null): void => {
  const csvContent = generateProjectSettingsCSV(project);
  
  if (!csvContent) {
    alert('No project settings to export');
    return;
  }
  
  // Create a Blob containing the CSV data
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const projectName = project?.name?.replace(/\s+/g, '_') || 'settings';
  
  // Set link attributes
  link.href = url;
  link.setAttribute('download', `${projectName}_settings.csv`);
  document.body.appendChild(link);
  
  // Trigger download
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
